import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { AlertCircle, Download, FileSpreadsheet } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { PROPERTY_TYPES } from "@/features/properties/services/properties";
import { downloadTemplate } from "./templateCsv";

interface UploadStepProps {
  file: File | null;
  parsedDataLength: number;
  onFileSelected: (file: File) => void;
}

export function UploadStep({ file, parsedDataLength, onFileSelected }: UploadStepProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      toast({
        title: "Invalid File",
        description: "Please select a CSV file",
        variant: "destructive",
      });
      return;
    }
    onFileSelected(selectedFile);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Upload CSV File
          </CardTitle>
          <Button onClick={downloadTemplate} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging ? "border-primary bg-primary/5" : "border-muted hover:border-primary/50"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <FileSpreadsheet className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <p className="font-medium mb-2">
            Drop your CSV file here or click to browse
          </p>
          <p className="text-sm text-muted-foreground mb-3">
            Download the template for proper formatting • Supports CSV files up to 10MB
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            className="hidden"
          />
          <Button onClick={() => fileInputRef.current?.click()} variant="outline">
            Browse Files
          </Button>
        </div>

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Format Requirements:</strong>
            <ul className="mt-1 space-y-1 text-sm">
              <li>• Required: Property Name, Address, Property Type</li>
              <li>• Property Types: {PROPERTY_TYPES.join(", ")}</li>
              <li>• Use decimal coordinates (e.g., 34.493015, -97.96416)</li>
            </ul>
          </AlertDescription>
        </Alert>

        {file && (
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB • {parsedDataLength} rows
                </p>
              </div>
              <Badge variant="secondary">Ready</Badge>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
